import { useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Tag, Button, Dialog, Table, Row, Cell, Pagination, Input } from '@dsi/react-eds';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { useLoader } from '../../hooks/useLoader';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { gnbDeployService, DeploymentNode, DeploymentNodeStatus } from '../../services/gnbDeployService';
import { useFetchLogs } from './log-viewer/useFetchLogs';
import StepsLogView from './log-viewer/StepsLogView';
import { useTheme } from '../../hooks/useTheme';
import socketService from '../../services/WebsocketService';
import { useWebSocketService } from '../../hooks/useWebSocketService';
import styles from './DeploymentStatusView.module.css';

interface DeploymentStatusViewProps {
  workflowId: string;
  deploymentId?: string;
}

const statusColorMap: Record<DeploymentNodeStatus, string> = {
  pending: 'yellow',
  running: 'blue',
  done: 'green',
  failed: 'red',
};

const statusLabel: Record<DeploymentNodeStatus, string> = {
  pending: 'Pending',
  running: 'Running',
  done: 'Done',
  failed: 'Failed',
};

export default function DeploymentStatusView({ workflowId, deploymentId }: DeploymentStatusViewProps) {
  const [nodes, setNodes] = useState<DeploymentNode[]>([]);
  const [total, setTotal] = useState(0);
  const [logDialog, setLogDialog] = useState<{ open: boolean; url: string; nodeName: string }>({ open: false, url: '', nodeName: '' });
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<{ nodeName?: string; draftName?: string; errorMessage?: string }>({});
  const [summary, setSummary] = useState<{ pending: number; running: number; done: number; failed: number }>({ pending: 0, running: 0, done: 0, failed: 0 });
  const { showLoader, hideLoader } = useLoader();
  const { addNotifications } = useSwobNotifications();
  const { theme } = useTheme();
  useWebSocketService();

  const fetchStatus = useCallback(async () => {
    try {
      showLoader();
      const [nodesRes, summaryRes] = await Promise.all([
        gnbDeployService.getDeploymentNodes(workflowId, deploymentId, {
          page,
          limit: pageSize,
          ...filters,
          sortBy,
          sortOrder,
        }),
        gnbDeployService.getDeploymentSummary(workflowId, deploymentId),
      ]);
      const result = nodesRes.data;
      if (result.data) {
        setNodes(result.data);
        setTotal(result.total || 0);
      }
      const s = summaryRes.data;
      setSummary({ pending: s.pending || 0, running: s.running || 0, done: s.done || 0, failed: s.failed || 0 });
    } catch (err) {
      addNotifications([{ title: 'Error', icon: 'icon-triangle-warning', description: `Failed to fetch deployment status: ${err instanceof Error ? err.message : 'Unknown error'}`, color: 'red', timestamp: new Date() }]);
    } finally {
      setTimeout(() => hideLoader(), 500);
    }
  }, [deploymentId, workflowId, page, pageSize, filters, sortBy, sortOrder]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

useEffect(() => {                                                                                                                                                                                                
    const unsubscribe = socketService.onEntityChange('GNB_DEPLOYMENT_NODE', (event) => {                                                                                                                           
      if (event.payload.workflowTicketId !== workflowId) return;                                                                                                                                                     
      const { summary: eventSummary } = event.payload;                                                                                                                                                             
                                                                                                                                                                                                                   
      // Handle batch or single                                                                                                                                                                                    
      const updates = event.payload.nodes                                                                                                                                                                          
        ? event.payload.nodes                                                                                                                                                                                      
        : [{ gnbNodeId: event.payload.gnbNodeId, status: event.payload.status, errorMessage: event.payload.errorMessage }];                                                                                        
                                                                                                                                                                                                                   
      setNodes((prev) => {                                                                                                                                                                                         
        const updated = [...prev];                                                                                                                                                                                 
        for (const { gnbNodeId, status, errorMessage } of updates) {                                                                                                                                               
          const idx = updated.findIndex((n) => n.gnbNodeId === gnbNodeId);                                                                                                                                         
          if (idx === -1) continue;                                                                                                                                                                                
          updated[idx] = {                                                                                                                                                                                         
            ...updated[idx],                                                                                                                                                                                       
            status,                                                                                                                                                                                                
            startedAt: status === 'running' ? updated[idx].startedAt || new Date().toISOString() : updated[idx].startedAt,                                                                                         
            completedAt: status === 'done' || status === 'failed' ? new Date().toISOString() : updated[idx].completedAt,                                                                                           
            errorMessage: errorMessage || updated[idx].errorMessage                                                                                                                                                
          };                                                                                                                                                                                                       
        }                                                                                                                                                                                                          
        return updated;                                                                                                                                                                                            
      });                                                                                                                                                                                                          
                                                                                                                                                                                                                   
      if (eventSummary) {                                                                                                                                                                                          
        setSummary({ pending: eventSummary.pending || 0, running: eventSummary.running || 0, done: eventSummary.done || 0, failed: eventSummary.failed || 0 });                                                    
        setTotal(prev => eventSummary.total || prev);                                                                                                                                                                     
      }                                                                                                                                                                                                            
    });                                                                                                                                                                                                            
    return unsubscribe;                                                                                                                                                                                            
  }, [deploymentId]);

  const numPages = Math.ceil(total / pageSize);
  const hasDeployedNodes = summary.done > 0 || summary.failed > 0 || summary.running > 0 || summary.pending > 0;

  const statusItems = useMemo(() => [
    { label: 'Done', value: summary.done, iconClass: styles.statusIconGreen },
    { label: 'Failed', value: summary.failed, iconClass: styles.statusIconRed },
    { label: 'Running', value: summary.running, iconClass: styles.statusIconBlue },
    { label: 'Pending', value: summary.pending, iconClass: styles.statusIconOrange },
  ], [summary]);

  return (
    <div>
      {hasDeployedNodes && (
        <>
          <h4 className={styles.heading}>Deployment Summary</h4>
          <div className={styles.summaryContainer}>
            {statusItems.map((item) => (
              <div key={item.label} className={styles.kpi}>
                <div className={styles.kpiValue}>{item.value}</div>
                <div className={styles.kpiStatus}>
                  <span className={`${styles.statusIcon} ${item.iconClass}`}></span>
                  <span>{item.label}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className={`${styles.heading} ${styles.tableHeader}`}>
        <h4>Node Status</h4>
        <div className={styles.filterButtons}>
          <ReactEDSWrapper component={Button} icon={showFilters ? 'filter-off' : 'filter'} onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? 'Close Filters' : 'Filters'}
          </ReactEDSWrapper>
          {showFilters && (
            <ReactEDSWrapper component={Button} icon="filter-clear" onClick={() => { setFilters({}); setPage(1); }}>
              Clear Filters
            </ReactEDSWrapper>
          )}
        </div>
      </div>
      {total > 0 && (
        <div className="table-top">
          <div className="table-top-left">
            <div className="table-info">
              <span>{(page - 1) * pageSize + 1} - {Math.min(page * pageSize, total)} of</span>
              <span> {total} items</span>
            </div>
          </div>
        </div>
      )}
      <ReactEDSWrapper
        component={Table}
        id="deployment-status-table"
        columns={[
          { title: 'Node Name', attribute: 'nodeName', sortable: true, sortOrder: sortBy === 'nodeName' ? sortOrder : undefined },
          { title: 'Status', attribute: 'status', sortable: true, sortOrder: sortBy === 'status' ? sortOrder : undefined },
          { title: 'Draft', attribute: 'draftName', sortable: true, sortOrder: sortBy === 'draftName' ? sortOrder : undefined },
          { title: 'Error', attribute: 'errorMessage', sortable: true, sortOrder: sortBy === 'errorMessage' ? sortOrder : undefined },
          { title: 'Started', attribute: 'startedAt', sortable: true, sortOrder: sortBy === 'startedAt' ? sortOrder : undefined },
          { title: 'Completed', attribute: 'completedAt', sortable: true, sortOrder: sortBy === 'completedAt' ? sortOrder : undefined },
          { title: 'Logs', attribute: 'logs' }
        ]}
        sortable
        onSort={(order: 'asc' | 'desc', attr: string) => { setSortBy(attr); setSortOrder(order); setPage(1); }}
        filters={showFilters ? (
          <ReactEDSWrapper component={Row} key="filter-row">
            <ReactEDSWrapper component={Cell}><ReactEDSWrapper component={Input} placeholder="Filter" fullwidth value={filters.nodeName || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setFilters((prev) => ({ ...prev, nodeName: e.target.value.trim() || undefined })); setPage(1); }} /></ReactEDSWrapper>
            <ReactEDSWrapper component={Cell}></ReactEDSWrapper>
            <ReactEDSWrapper component={Cell}><ReactEDSWrapper component={Input} placeholder="Filter" fullwidth value={filters.draftName || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setFilters((prev) => ({ ...prev, draftName: e.target.value.trim() || undefined })); setPage(1); }} /></ReactEDSWrapper>
            <ReactEDSWrapper component={Cell}><ReactEDSWrapper component={Input} placeholder="Filter" fullwidth value={filters.errorMessage || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setFilters((prev) => ({ ...prev, errorMessage: e.target.value.trim() || undefined })); setPage(1); }} /></ReactEDSWrapper>
            <ReactEDSWrapper component={Cell}></ReactEDSWrapper>
            <ReactEDSWrapper component={Cell}></ReactEDSWrapper>
            <ReactEDSWrapper component={Cell}></ReactEDSWrapper>
          </ReactEDSWrapper>
        ) : undefined}
        showFilters={showFilters}
      >
        {nodes.length > 0 ? nodes.map((node) => (
          <ReactEDSWrapper component={Row} key={node.id}>
            <ReactEDSWrapper component={Cell}>{node.gnbNode?.nodeName || node.gnbNodeId}</ReactEDSWrapper>
            <ReactEDSWrapper component={Cell}><ReactEDSWrapper component={Tag} color={statusColorMap[node.status]} big>{statusLabel[node.status]}</ReactEDSWrapper></ReactEDSWrapper>
            <ReactEDSWrapper component={Cell}>{node.draftName || '—'}</ReactEDSWrapper>
            <ReactEDSWrapper component={Cell}><span className={node.errorMessage ? styles.errorText : styles.timestamp}>{node.errorMessage || '—'}</span></ReactEDSWrapper>
            <ReactEDSWrapper component={Cell}><span className={styles.timestamp}>{node.startedAt ? new Date(node.startedAt).toLocaleString() : '—'}</span></ReactEDSWrapper>
            <ReactEDSWrapper component={Cell}><span className={styles.timestamp}>{node.completedAt ? new Date(node.completedAt).toLocaleString() : '—'}</span></ReactEDSWrapper>
            <ReactEDSWrapper component={Cell}>{node.logUrl ? <a href="#" onClick={(e) => { e.preventDefault(); setLogDialog({ open: true, url: node.logUrl!, nodeName: node.gnbNode?.nodeName || node.gnbNodeId }); }} className={styles.viewLink}>View</a> : '—'}</ReactEDSWrapper>
          </ReactEDSWrapper>
        )) : (
          <ReactEDSWrapper component={Row}>
            <ReactEDSWrapper component={Cell} colSpan={7}>
              <div className={styles.emptyState}>
                <i className={`icon icon-info ${styles.emptyStateIcon}`} />
                <div className={styles.emptyStateTitle}>No nodes deployed</div>
                <div className={styles.emptyStateSubtitle}>Deployment status will appear here once a deployment is submitted.</div>
              </div>
            </ReactEDSWrapper>
          </ReactEDSWrapper>
        )}
      </ReactEDSWrapper>
      {numPages > 1 && (
        <ReactEDSWrapper
          component={Pagination}
          numPages={numPages}
          selectedPage={page}
          onPaginationChangePage={setPage}
          optionsList={[
            { value: '20', label: '20', active: pageSize === 20 },
            { value: '50', label: '50', active: pageSize === 50 },
            { value: '100', label: '100', active: pageSize === 100 },
          ]}
          onPaginationChangeSelect={(size: string) => { setPageSize(Number(size)); setPage(1); }}
          withActions
        />
      )}

      {logDialog.open && <LogDialog url={logDialog.url} nodeName={logDialog.nodeName} theme={theme} onClose={() => setLogDialog({ open: false, url: '', nodeName: '' })} />}
    </div>
  );
}

function LogDialog({ url, nodeName, theme, onClose }: { url: string; nodeName: string; theme: boolean; onClose: () => void }) {
  const { logs, isLoading, error } = useFetchLogs(url);

  return ReactDOM.createPortal(
    <ReactEDSWrapper
      component={Dialog}
      show
      title={`Logs — ${nodeName}`}
      onClose={onClose}
      data-type="fullscreen" className="fullscreen"
      bottom={<ReactEDSWrapper component={Button} primary onClick={onClose}>Close</ReactEDSWrapper>}
    >
      <div className={styles.logDialogContent}>
        <StepsLogView logs={logs} isLoading={isLoading} error={error} isStepCompletedButStageRunning={false} leftPanelOpen={false} theme={theme} />
      </div>
    </ReactEDSWrapper>,
    document.body
  );
}
